// user-select.component.ts
import { UserData } from '@/models/user-data.model';
import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  forwardRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
  FormsModule,
} from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { UserAvatarComponent } from './user-avatar.component';


@Component({
  selector: 'app-user-select',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    SelectModule,
    UserAvatarComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserSelectComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => UserSelectComponent),
      multi: true,
    },
  ],
  template: `
<p-select
  styleClass="w-full std-input"
  [options]="users ?? []"
  [placeholder]="placeholder"
  [optionLabel]="optionLabelKey"
  [optionValue]="optionValueKey"
  [dataKey]="optionValueKey"
  [disabled]="disabled"
  [(ngModel)]="innerValue"
  (ngModelChange)="onValueChange($event)"
  (onBlur)="handleBlur()"
  [filter]="enableClientFilter"
  [filterFields]="filterFields"
>
  <!-- Selected item -->
  <ng-template #selectedItem let-selectedOption>
    <div class="flex items-center gap-2" *ngIf="selectedOption">
      <app-user-avatar [user]="selectedOption" [showLabel]="false"></app-user-avatar>
      <div>{{ selectedOption[optionLabelKey] }}</div>
    </div>
  </ng-template>

  <!-- Option row -->
  <ng-template #item let-user>
    <div class="flex items-center gap-2">
      <app-user-avatar [user]="user" [showLabel]="false"></app-user-avatar>
      <div>{{ user[optionLabelKey] }}</div>
    </div>
  </ng-template>

  <ng-template #header>
    <div class="font-medium p-3">{{ headerText }}</div>
  </ng-template>
</p-select>
  `,
  styles: [
    `
    .std-input {
      height: 40px;
    }
    `
  ]
})
export class UserSelectComponent
  implements ControlValueAccessor, OnChanges
{
  /** Pre-filtered list from the parent (dumb component) */
  @Input() users: UserData[] | null | undefined;

  /** UI options */
  @Input() placeholder: string = 'Select a user';
  @Input() headerText: string = 'Available Users';

  /** Which keys to use for label/value */
  @Input() optionLabelKey: string = 'DisplayName';
  @Input() optionValueKey: string = 'UID';

  /** Client-side filtering (search in dropdown) */
  @Input() enableClientFilter: boolean = true;
  @Input() filterFields: (keyof UserData)[] = ['DisplayName'];

  /** Mark as required to surface validation */
  @Input() required: boolean = false;

  /**
   * If the parent provides a new `users` list that does not contain
   * the currently selected id, clear the selection when false.
   * When true, we keep the stale id until the parent changes it.
   */
  @Input() allowStaleValue: boolean = false;

  // CVA internal state
  innerValue: string | number | null = null;
  disabled = false;

  // CVA callbacks
  private onChange: (value: string | number | null) => void = () => {};
  private onTouched: () => void = () => {};

  // ---- ControlValueAccessor ----
  writeValue(value: string | number | null): void {
    this.innerValue = value ?? null;
  }

  registerOnChange(fn: (value: string | number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ---- Validation passthrough (optional) ----
  validate(_: AbstractControl): ValidationErrors | null {
    if (this.required && (this.innerValue === null || this.innerValue === undefined || this.innerValue === '')) {
      return { required: true };
    }
    return null;
  }

  // ---- Events ----
  onValueChange(value: string | number | null): void {
    this.innerValue = value;
    this.onChange(value);
  }

  handleBlur(): void {
    this.onTouched();
  }

  // ---- React to parent-provided user list changes ----
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['users'] && this.users && !this.allowStaleValue) {
      const key = this.optionValueKey as string;
      const exists = this.users.some(u => (u as any)[key] === this.innerValue);
      if (!exists) {
        this.innerValue = null;
        this.onChange(this.innerValue);
      }
    }
  }
}
